/**
 * Kiosco: la tablet compartida a la entrada de la nave.
 *
 * Para quien no tiene móvil de empresa. Se usa de pie, en cuatro segundos y
 * con cola detrás: teclado enorme, PIN, foto de verificación y a la calle.
 *
 * Vuelve solo a la pantalla inicial a los 5 s — si no, el siguiente se
 * encuentra el resultado del anterior en pantalla, que además de incómodo es
 * enseñar el nombre de un compañero a todo el que pase.
 *
 * El kiosco no lleva sesión: el PIN es la autenticación (enterprise.py). El
 * kioskId identifica la tablet y se guarda al darla de alta.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/apiClient';
import { Icon } from './ui/Icon';
import { ThemeToggle } from './ui/ThemeToggle';

// El backend acepta PINs de 4 a 6 dígitos (`SetPINRequest`, `set_user_pin`) y
// esta pantalla aceptaba EXACTAMENTE 4, con envío automático al cuarto: a quien
// tuviera un PIN de 5 o 6 se le mandaba truncado y recibía "PIN incorrecto"
// para siempre, con cada intento consumiendo el presupuesto de bloqueo. Cuatro
// capas de acuerdo y la pantalla diciendo otra cosa.
const PIN_MIN = 4;
const PIN_MAX = 6;
const RESET_MS = 5000;
const KIOSK_ID_KEY = 'kakuat-kiosk-id';
const KIOSK_SITE_KEY = 'kakuat-kiosk-worksite';

type Result = { ok: boolean; message: string; warn?: string } | null;
// Los cuatro actos que el backend acepta en `/kiosk/punch` (el pattern de
// KioskPunchRequest ya admite break_start/break_end). Para quien no tiene
// móvil, la tablet es su única puerta: si aquí no se puede pausar, la pausa
// real del descanso no queda como marca y el registro miente sobre el
// descanso efectivo (ronda 4, r2b09[4]). La pantalla no sabe el estado del
// trabajador ANTES del PIN — no hay sesión —, así que se ofrecen las cuatro
// y la máquina de estados del backend decide: una pausa sin jornada abierta
// vuelve como 409 con su mensaje humano.
type Direction = 'in' | 'out' | 'break_start' | 'break_end';

export function KioskPage() {
  // El backend NO alterna entrada/salida: usa el punch_type que le llega.
  // Por eso el trabajador elige primero qué viene a hacer — un toque más,
  // pero sin él cada salida sería una segunda entrada.
  const [direction, setDirection] = useState<Direction | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // El alta de la tablet entra por la URL: el admin genera el enlace desde el
  // panel y se abre una vez en el dispositivo. Antes no había NINGUNA forma de
  // escribir esta clave —ninguna pantalla llamaba a `/kiosk/register` ni hacía
  // `setItem`—, así que el kiosco caía siempre en "Tablet sin dar de alta"
  // salvo que alguien tecleara el id en la consola del navegador.
  const [kioskId, setKioskId] = useState<string | null>(() =>
    localStorage.getItem(KIOSK_ID_KEY),
  );
  const [worksiteUuid, setWorksiteUuid] = useState<string | null>(() =>
    localStorage.getItem(KIOSK_SITE_KEY),
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('kiosk_id');
    if (!id) return;
    localStorage.setItem(KIOSK_ID_KEY, id);
    setKioskId(id);
    const site = params.get('worksite');
    if (site) {
      localStorage.setItem(KIOSK_SITE_KEY, site);
      setWorksiteUuid(site);
    }
    // Fuera de la barra de direcciones: el kiosk_id es una credencial.
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // La cámara vive todo el turno: abrirla por fichaje añadiría 2 s de espera
  // a cada persona de la cola.
  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'user' } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch(() => {
        // Sin cámara se ficha igual: la foto es verificación, no requisito.
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (!result) return;
    const id = setTimeout(() => {
      setResult(null);
      setPin('');
      setDirection(null);
    }, RESET_MS);
    return () => clearTimeout(id);
  }, [result]);

  const capture = (): string | undefined => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return undefined;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
  };

  const submit = useCallback(
    async (fullPin: string) => {
      setBusy(true);
      // Se captura ANTES de enviar: si había foto y el backend dice que no
      // pudo guardarla, hay que avisarlo — es la prueba para la disputa de
      // dentro de 80 días. Si no había cámara, la foto no es requisito y no
      // se dice nada (r2b09[6]).
      const foto = capture();
      try {
        const res = await apiClient.post<{
          punch_type?: string;
          user_name?: string;
          photo_stored?: boolean;
        }>(
          // ⚠️ SIN `/enterprise`: este router se monta sin prefijo y sus rutas se
          // escriben enteras (`docs/02-api.md`). La PWA llamaba a
          // `/enterprise/kiosk/punch`, que no existe: **el fichaje por quiosco
          // nunca ha podido funcionar**, 404 en cada intento. No salió en
          // ningún informe de la ronda —el bloque 9 auditó el quiosco entero y
          // dio la ruta por buena— y lo cazó el primer test que llamó al
          // endpoint por HTTP.
          '/kiosk/punch',
          {
            pin: fullPin,
            punch_type: direction ?? 'in',
            photo_base64: foto,
            kiosk_id: kioskId,
            worksite_uuid: worksiteUuid,
          }
        );
        const who = res.data.user_name ? `, ${res.data.user_name}` : '';
        setResult({
          ok: true,
          message: `Fichaje registrado${who}`,
          warn:
            foto && res.data.photo_stored === false
              ? 'No se pudo guardar la foto de verificación: avisa a tu responsable'
              : undefined,
        });
      } catch (err: unknown) {
        // Un error sin `response` no llegó al servidor (red caída, DNS,
        // timeout): el PIN no se probó contra nada y NINGÚN contador lo
        // anotó. El fallback era «PIN incorrecto» para CUALQUIER fallo, y el
        // trabajador reintentaba contra su PIN correcto desconfiando del
        // sistema (r2b09[5]). El 413 del proxy (la foto no cabe en el body)
        // tampoco es culpa del PIN: mensaje propio (r2b09[1]).
        const respuesta = (
          err as { response?: { status?: number; data?: { detail?: string } } }
        )?.response;
        let message: string;
        if (!respuesta) {
          message = 'No hay conexión con el servidor. Vuelve a intentarlo.';
        } else if (respuesta.status === 413) {
          message = 'La foto no cabe en la conexión. Avisa a tu responsable.';
        } else {
          message = respuesta.data?.detail ?? 'PIN incorrecto';
        }
        setResult({ ok: false, message });
      } finally {
        setBusy(false);
        setPin('');
      }
    },
    [kioskId, worksiteUuid, direction]
  );

  const press = (key: string) => {
    if (busy || result) return;
    if ('vibrate' in navigator) navigator.vibrate(30);

    if (key === 'del') {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (key === 'ok') {
      if (pin.length >= PIN_MIN) submit(pin);
      return;
    }
    setPin((p) => (p.length >= PIN_MAX ? p : p + key));
  };

  // Ya no se envía solo al cuarto dígito: con PINs de 4 a 6 no se puede saber
  // cuándo ha terminado de teclear. Se pulsa OK, que además se habilita en
  // cuanto el PIN es válido.

  if (!kioskId) {
    return (
      <div className="min-h-screen grid place-items-center px-8 text-center">
        <div>
          <Icon name="kiosk" size={48} className="text-ink-3 mx-auto mb-4" />
          <h1 className="font-display font-extrabold text-[24px] mb-2">Tablet sin dar de alta</h1>
          <p className="text-ink-2 text-[15px] max-w-[34ch]">
            Un administrador tiene que registrar este kiosco antes de poder fichar en él.
          </p>
        </div>
      </div>
    );
  }

  const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'del', '0', 'ok'];

  return (
    <div className="min-h-screen flex flex-col bg-surface px-6 pt-7 pb-8 safe-area-top safe-area-bottom">
      <video ref={videoRef} playsInline muted className="hidden" />
      <canvas ref={canvasRef} className="hidden" />

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <span className="w-3 h-3 rounded-full bg-brand" />
          <span className="font-display font-black text-[20px]">kakuat</span>
        </div>
        <ThemeToggle />
      </div>

      {result ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 text-center animate-rise">
          <div
            className={`w-24 h-24 rounded-full grid place-items-center ${
              result.ok ? 'bg-brand-soft' : 'bg-danger-soft'
            }`}
          >
            <Icon
              name={result.ok ? 'shield' : 'alert'}
              size={44}
              className={result.ok ? 'text-brand' : 'text-danger'}
            />
          </div>
          <p
            className={`font-display font-extrabold text-[26px] ${
              result.ok ? 'text-brand' : 'text-danger'
            }`}
          >
            {result.message}
          </p>
          {result.warn && (
            <p className="text-[15px] text-warn font-semibold -mt-3">{result.warn}</p>
          )}
          <p className="font-mono text-[12px] text-ink-3">Volviendo al inicio…</p>
        </div>
      ) : direction === null ? (
        <div className="flex-1 flex flex-col justify-center gap-4 animate-rise">
          <p className="text-center font-mono text-[12px] uppercase tracking-[0.14em] text-ink-3">
            Kiosco · ¿Qué vienes a hacer?
          </p>
          <button
            onClick={() => setDirection('in')}
            className="h-[110px] rounded-3xl bg-brand text-on-brand font-display font-black text-[32px] flex items-center justify-center gap-3.5"
          >
            <Icon name="play" size={36} strokeWidth={1.6} />
            ENTRADA
          </button>
          <button
            onClick={() => setDirection('out')}
            className="h-[110px] rounded-3xl bg-danger text-on-danger font-display font-black text-[32px] flex items-center justify-center gap-3.5"
          >
            <Icon name="stop" size={36} strokeWidth={1.6} />
            SALIDA
          </button>
          {/* Pausas: mismos botones grandes que entrada y salida — es un
              acto de fichaje como los otros, no un ajuste escondido. Sin
              motivo de pausa (la tablet no pregunta): el motor la trata
              como NO pagada, que es la lectura conservadora (el tiempo de
              descanso no cuenta como trabajado). */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setDirection('break_start')}
              className="h-[76px] rounded-2xl border-[1.5px] border-line-strong bg-surface text-ink font-display font-extrabold text-[19px] flex items-center justify-center gap-2.5"
            >
              <Icon name="clock" size={24} strokeWidth={1.8} />
              INICIAR PAUSA
            </button>
            <button
              onClick={() => setDirection('break_end')}
              className="h-[76px] rounded-2xl border-[1.5px] border-line-strong bg-surface text-ink font-display font-extrabold text-[19px] flex items-center justify-center gap-2.5"
            >
              <Icon name="play" size={24} strokeWidth={1.8} />
              REANUDAR
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-center my-4">
            <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-ink-3">
              Kiosco ·{' '}
              {direction === 'in'
                ? 'Entrada'
                : direction === 'out'
                  ? 'Salida'
                  : direction === 'break_start'
                    ? 'Iniciar pausa'
                    : 'Reanudar'}
              {' · '}
              <button onClick={() => { setDirection(null); setPin(''); }} className="underline">cambiar</button>
            </p>
            <p className="font-display font-extrabold text-[24px] mt-1">
              {busy ? 'Comprobando…' : 'Introduce tu PIN'}
            </p>
          </div>

          <div className="flex justify-center gap-3.5 mb-2">
            {Array.from({ length: PIN_MAX }).map((_, i) => (
              <span
                key={i}
                className={`w-[18px] h-[18px] rounded-full border-2 ${
                  i < PIN_MIN ? 'border-line-strong' : 'border-dashed border-line'
                }`}
                style={{ background: i < pin.length ? 'var(--brand)' : 'transparent' }}
              />
            ))}
          </div>
          <p className="text-center font-mono text-[12px] text-ink-3 mb-5">
            de 4 a 6 dígitos · pulsa OK al terminar
          </p>

          <div className="grid grid-cols-3 gap-3 flex-1">
            {KEYS.map((k) => (
              <button
                key={k}
                onClick={() => press(k)}
                disabled={busy}
                aria-label={k === 'del' ? 'Borrar' : k === 'ok' ? 'Confirmar' : k}
                className="rounded-2xl border border-line font-display font-bold text-[30px] grid place-items-center min-h-[64px] disabled:opacity-40"
                style={{
                  background:
                    k === 'ok' ? 'var(--brand-soft)' : k === 'del' ? 'transparent' : 'var(--canvas)',
                }}
              >
                {k === 'del' ? '⌫' : k === 'ok' ? '✓' : k}
              </button>
            ))}
          </div>

          <p className="flex items-center gap-2.5 justify-center mt-4 text-ink-2 text-[13.5px]">
            <Icon name="camera" size={20} strokeWidth={1.7} />
            Se tomará una foto de verificación
          </p>
        </>
      )}
    </div>
  );
}
